<template>
  <Transition name="gambit-overlay">
    <div v-if="isOpen" class="GambitOverlay" @click.self="handleClose">
      <div ref="drawerRef" class="gambit-card" :class="{ 'is-mobile': isMobile }">
        <div class="gambit-hero">
          <img
            v-if="gambit.locationImageBlobHash"
            class="gambit-hero-img"
            :src="getBlobImage(gambit.locationImageBlobHash, 400)"
            loading="lazy"
          />
        </div>

        <div class="gambit-scroll" :class="{ 'chat-open': isChatOpen }">
          <div class="gambit-header">
            <div v-if="gambit.location" class="gambit-spot-info" @click="handleOpenSpot">
              <LocationCircle
                v-if="circleLocation"
                :location="circleLocation"
                :selected="!isConcluded"
                mode="none"
                class="gambit-spot-circle"
              />
            </div>

            <div class="gambit-owner-actions">
              <GamGambitOwner v-if="isOrganizer || isCreator" is-club-organizer is-detail is-darker />
              <GamMore v-bind="getMoreItems" style="--button-full-width: 170px" />
            </div>
          </div>

          <div v-if="!isConcluded" class="gambit-time-line" :class="{ 'is-soon': isTimeSoon }">
            <span>{{ formattedTime }}</span>
          </div>

          <div v-if="!isConcluded" class="gambit-actions-row" :class="{ 'is-stacked': isAttendeeActionsStacked }">
            <template v-if="!isChallenge || isConfirmedChallenge">
              <div class="gambit-actions-primary">
                <GamButton
                  v-if="showJoinButton"
                  class="gambit-play-cta play-cta play-cta-gambit"
                  :variant="GamButtonVariant.SLAB"
                  :size="GamButtonSize.LARGE"
                  :left-icon="GamIconName.PLAY_EMPTY"
                  is-icon-only
                  is-drop-shadow
                  @click="handleJoinGambit"
                />

                <GamButton
                  v-if="gambit.isAttending"
                  class="gambit-play-cta play-cta play-cta-gambit is-inverted"
                  :variant="GamButtonVariant.SLAB"
                  :size="GamButtonSize.LARGE"
                  :left-icon="GamIconName.PAUSE"
                  is-icon-only
                  is-drop-shadow
                  @click="handleLeaveGambit"
                />
              </div>

              <div class="gambit-actions-secondary">
                <div
                  v-if="gambit.isAttending"
                  class="gambit-action-btn gambit-action-secondary has-status"
                  :class="{ 'is-on': myChessSetCount > 0, 'has-warning': isShortOfChessSets }"
                  @click="openChessSetPopover"
                >
                  <GamIcon :name="GamIconName.CHESS_SET" is-current />
                </div>

                <div
                  v-if="isChatAllowed && gambit.chatId"
                  class="gambit-action-btn gambit-action-secondary"
                  :class="{ 'is-on': hasUnreadChat }"
                  @click="isChatSynced && chat ? (isChatOpen ? handleChatClose() : handleChatOpen()) : undefined"
                >
                  <GamIcon :name="GamIconName.MESSAGE" is-current />
                  <span v-if="hasUnreadChat" class="unread-indicator">{{ unreadChatCountLabel }}</span>
                </div>

                <div
                  v-if="showCheckInButton"
                  class="gambit-action-btn gambit-action-secondary"
                  :class="{ 'is-on': isCheckedIn }"
                  @click="isCheckedIn ? handleCheckOut() : handleCheckIn()"
                >
                  <GamIcon :name="GamIconName.PIN" is-current />
                </div>
              </div>
            </template>

            <template v-if="isPendingChallenge">
              <template v-if="isChallenger">
                <GamButton
                  class="play-cta play-cta-withdraw"
                  :size="GamButtonSize.SMALL"
                  :variant="GamButtonVariant.SLAB"
                  :is-loading="isDeleting"
                  :confirmation="localize('gambit.challenge.actions.withdraw.confirmation')"
                  @click="handleDelete"
                >
                  {{ localize('gambit.challenge.actions.withdraw') }}
                </GamButton>
                <GamButton
                  class="play-cta play-cta-change is-inverted"
                  :size="GamButtonSize.SMALL"
                  :variant="GamButtonVariant.SLAB"
                  @click="handleChange"
                >
                  {{ localize('gambit.challenge.actions.change.mobile') }}
                </GamButton>
              </template>
              <template v-else>
                <GamButton
                  class="play-cta play-cta-decline is-inverted"
                  :size="GamButtonSize.SMALL"
                  :variant="GamButtonVariant.SLAB"
                  :is-loading="isDeleting"
                  :confirmation="localize('gambit.challenge.actions.decline.confirmation')"
                  @click="handleDelete"
                >
                  {{ localize('gambit.challenge.actions.decline') }}
                </GamButton>
                <GamButton
                  class="play-cta play-cta-change is-inverted"
                  :size="GamButtonSize.SMALL"
                  :variant="GamButtonVariant.SLAB"
                  @click="handleChange"
                >
                  {{ localize('gambit.challenge.actions.change.mobile') }}
                </GamButton>
                <GamButton
                  class="play-cta play-cta-gambit"
                  :size="GamButtonSize.SMALL"
                  :variant="GamButtonVariant.SLAB"
                  :is-loading="isAccepting"
                  @click="handleAccept"
                >
                  {{ localize('gambit.challenge.actions.accept') }}
                </GamButton>
              </template>
            </template>
          </div>

          <div v-if="isConcluded" class="gambit-concluded-label">
            <GamIcon :name="GamIconName.LAST_TIME" :size="GamIconSize.LARGE" is-primary />
            {{ concludedAgo }}
          </div>

          <ExpandableDescription v-if="gambit.description" class="gambit-description">
            {{ gambit.description.trim() }}
          </ExpandableDescription>

          <div v-if="hasCustomTitle" class="gambit-title">
            {{ gambit.title }}
          </div>

          <template v-if="isPublicGambit && (attendees.length || invitees.length)">
            <div v-if="gambitTags.length" class="gambit-section-header">
              <div class="gambit-tags">
                <GamChip
                  v-for="(tag, index) in gambitTags"
                  v-bind="tag"
                  :key="`tag-${index}`"
                  :is-active="gambit.attendeeCount >= 1 && index === 0"
                  is-upper-case
                />
              </div>
            </div>
            <hr class="gambit-section-line" />

            <AttendeeList
              class="gambit-attendees"
              :user="user!"
              :attendees="attendees"
              :invitees="invitees"
              :gambit="gambit"
              :location="gambitLocation"
              :is-lesson="isLesson"
              :is-stream="isStream"
              :handshakes="gambit.handshakes || []"
              :pending-handshakes="gambit.pendingHandshakes || []"
              :active-check-ins="gambit.activeCheckIns || []"
              :handshake-eligible-check-ins="gambit.handshakeEligibleCheckIns || []"
              :recent-check-ins="gambit.recentCheckIns || []"
              :can-invite="(gambit.isAttending || isOrganizer) && !isPendingChallenge && !isConcluded"
              @update="gambitStore.fetch(gambit.id)"
              @invite="openInviteModal()"
            />
          </template>

          <template v-if="isPendingChallenge">
            <div v-if="gambitTags.length" class="gambit-section-header">
              <div class="gambit-tags">
                <GamChip
                  v-for="(tag, index) in gambitTags"
                  v-bind="tag"
                  :key="`pending-tag-${index}`"
                  :is-active="gambit.attendeeCount >= 1 && index === 0"
                  is-upper-case
                />
              </div>
            </div>
            <hr class="gambit-section-line" />

            <div class="gambit-challenge-attendees">
              <GamAttendeeRow
                v-for="(attendee, a) in attendees"
                :key="`attendee-${a}`"
                :player="attendee"
                :chess-set-count="attendee?.chessSetCount || 0"
                :is-organizer="attendee?.id === gambit.ownerId || attendee?.isOrganizer"
                :is-confirmed="challengeInvitation?.withdrawn !== true && challengeInvitation?.accepted !== false"
                :is-withdrawn="challengeInvitation?.withdrawn === true"
                show-unpaired-badge
              />

              <ChallengedPlayer
                v-if="challengeInvitation && !gambit.isConfirmed"
                :id="challengeInvitation.userId"
                :class="{ 'is-first': isChallenged }"
                :player="challengeInvitation.user"
                is-organizer
                :is-waiting="challengeInvitation.accepted === null"
                :is-confirmed="challengeInvitation.accepted === true && challengeInvitation.withdrawn !== true"
                :is-declined="challengeInvitation.accepted === false"
              />
            </div>
          </template>

          <template v-if="partiallyAnonimizedHandshakes.length > 0">
            <div class="gambit-section-header">
              <span class="gambit-section-title">Handshakes</span>
            </div>
            <hr class="gambit-section-line" />

            <HandshakeBoard
              class="gambit-handshake-board"
              :handshakes="partiallyAnonimizedHandshakes"
              :length="10"
              is-truncated
            />
          </template>

          <template v-if="substantiveHandshakes.length > 0">
            <div class="gambit-section-header">
              <span class="gambit-section-title">List of Handshakes</span>
            </div>
            <hr class="gambit-section-line" />

            <HandshakeList
              :handshakes="substantiveHandshakes.map((h) => ({ ...h, gambit }))"
              enable-game-suggestion
              @update="gambitStore.fetch(gambit.id)"
            />
          </template>
        </div>

        <Transition name="chat-drawer">
          <div v-if="isChatOpen && chat && areMessagesInitialized" class="gambit-chat-drawer">
            <Chat
              :is-enabled="isChatEnabled"
              :is-organizer="gambit.canEdit"
              :chat="chat"
              :title="gambit.title || gambit.location?.name || 'Chat'"
              @close="handleChatClose"
              :key="gambit.id"
            />
          </div>
        </Transition>

        <div v-if="!isChatOpen" class="gambit-close" @click="handleClose">
          <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.X_LARGE" is-current />
        </div>
      </div>
    </div>
  </Transition>

  <GamChessSetPopover
    v-model:open="showChessSetPopover"
    v-model="attendeeChessSetCount"
    :player-chess-sets="playerChessSets"
    :spot-chess-sets="spotChessSets"
    @confirm="confirmChessSet"
  />

  <ReportingDialog
    v-if="showReportDialog"
    :id="gambit.id"
    :type="ReportType.GAMBIT"
    @close="showReportDialog = false"
  />

  <GamDialog ref="qrCodeDialog">
    <SpotQRCodeDialog :spot-name="gambit.title || gambit.location?.name || ''" :url="getQrCodeValue(gambit.id)" />
  </GamDialog>

  <LeaveGambitConfirmModal
    v-if="isLeaveConfirmOpen"
    :title="leaveConfirmTitle"
    @close="dismissLeaveConfirm"
    @keep="dismissLeaveConfirm"
    @confirm="confirmLeaveGambit"
  />
</template>

<script setup lang="ts">
import type { BaseGambitDto } from '@/core/data/gambit/gambit.interface';
import { GambitCategory, GambitInvitationType } from '@/core/data/gambit/gambit.type';
import type { BaseLocationDto, NextGambitDto, ShallowLocationDto } from '@/core/data/location/location.interface';
import { LocationChessFeature, MapLocationType } from '@/core/data/location/location.type';
import { ReportType } from '@/core/data/report/report.type';
import type { AttendeeDto } from '@/core/data/user/user.interface';
import { gambits, localize, localizeWithValues } from '@/core/gambits';
import GambitHelper from '@/core/helpers/gambit.helper';
import { isMobile } from '@/core/helpers/ui.helper';
import { useAuthStore } from '@/stores/auth.store';
import { useChatStore } from '@/stores/chat.store';
import { useGambitStore } from '@/stores/gambit.store';
import { useMapStore } from '@/stores/map.store';
import { useNavigationStore } from '@/stores/navigation.store';
import { useSparkStore } from '@/stores/spark.store';
import ExpandableDescription from '@/views/components//ExpandableDescription.vue';
import ReportingDialog from '@/views/components/gam-dialog/dialog-contents/ReportingDialog.vue';
import SpotQRCodeDialog from '@/views/components/gam-dialog/dialog-contents/SpotQRCodeDialog.vue';
import GamDialog from '@/views/components/gam-dialog/GamDialog.vue';
import GamGambitOwner from '@/views/components/gam-gambits/GamGambitOwner.vue';
import LocationCircle from '@/views/components/gam-location/LocationCircle.vue';
import GamMore from '@/views/components/gam-nav/gam-more/GamMore.vue';
import GamAttendeeRow from '@/views/components/gam-player/GamAttendeeRow.vue';
import GamButton from '@/views/components/GamButton.vue';
import GamChessSetPopover from '@/views/components/GamChessSetPopover.vue';
import GamChip from '@/views/components/GamChip.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamChipSize } from '@/views/composables/constants/components/gamChip.constants';
import { GamDateType } from '@/views/composables/constants/components/gamGambit.constants';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { GamMorePosition } from '@/views/composables/constants/components/gamMore.constants';
import { useDrawerGesture } from '@/views/composables/hooks/useDrawerGesture';
import { useLeaveGambitConfirm } from '@/views/composables/hooks/useLeaveGambitConfirm';
import type { GambitDetailDto } from '@/views/composables/models/components/GamGambit';
import type { GamMoreItem, GamMoreType } from '@/views/composables/models/components/GamMore';
import Chat from '@/views/main/chat/Chat.vue';
import AttendeeList from '@/views/main/gambits/AttendeeList.vue';
import ChallengedPlayer from '@/views/main/gambits/forms/ChallengedPlayer.vue';
import { isInGambitCheckInWindow } from '@/views/main/gambits/utils/gambit-check-in-window';
import HandshakeBoard from '@/views/main/handshakes/HandshakeBoard.vue';
import HandshakeList from '@/views/main/handshakes/HandshakeList.vue';
import { getBlobImage } from '@/views/main/sparks/utils/image-blob';
import { formatDistanceToNow, isAfter, isBefore } from 'date-fns';
import { storeToRefs } from 'pinia';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useModal } from 'vue-final-modal';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import InvitePlayers, { type ChosenPlayer } from './modals/InvitePlayers.vue';
import LeaveGambitConfirmModal from './modals/LeaveGambitConfirmModal.vue';

export interface GambitOverlayProps {
  gambit: GambitDetailDto;
  isOpen: boolean;
}

const { gambit } = defineProps<GambitOverlayProps>();
const emit = defineEmits<{ close: [] }>();

const drawerRef = ref<HTMLElement | null>(null);
useDrawerGesture(drawerRef, () => emit('close'));

const authStore = useAuthStore();
const { user, userDetails } = storeToRefs(authStore);
const gambitStore = useGambitStore();
const mapStore = useMapStore();
const { currentLocation, currentZoom } = storeToRefs(mapStore);
const chatStore = useChatStore();
const { chat, areMessagesInitialized } = storeToRefs(chatStore);
const navigationStore = useNavigationStore();
const sparkStore = useSparkStore();

const route = useRoute();
const router = useRouter();

const nowDate = ref(new Date());

const isChatOpen = computed(() => route.query.chat === 'open');
const isChatEnabled = computed(() => gambit.attendeeCount > 1 && Boolean(chat.value?.isActive));
const isChatAllowed = computed(() => GambitHelper.isChatAccessible(gambit, nowDate.value));
const isChatSynced = computed(() => gambit.chatId && chat.value?.id === gambit.chatId);

const isConcluded = computed(() => (gambit.end ? nowDate.value > new Date(gambit.end) : false));

const isPublicGambit = computed(() => !gambit.isChallenge || gambit.isConfirmed);
const isPendingChallenge = computed(() => gambit.isChallenge && !gambit.isConfirmed);
const isConfirmedChallenge = computed(() => gambit.isChallenge && gambit.isConfirmed);
const isAttendeeActionsStacked = computed(
  () => gambit.isAttending && (!isChallenge.value || isConfirmedChallenge.value),
);
const isLesson = computed(() => gambit.gambitCategory === GambitCategory.LESSON);
const isStream = computed(() => gambit.streamed);
const isCreator = computed(() => typeof gambit.ownerId === 'string' && gambit.ownerId === user.value?.id);
const isOrganizer = computed(() => gambit.canEdit);

const challengeInvitation = computed(() => gambit.invitations.find((i) => i.type === GambitInvitationType.CHALLENGE));
const isChallenger = computed(() => challengeInvitation.value && gambit.ownerId === user.value?.id);
/** You are the one challenged, so your row comes before the challenger's. */
const isChallenged = computed(() => !!user.value?.id && challengeInvitation.value?.userId === user.value.id);
const isChallenge = computed(() => gambit.isChallenge);
const gambitLocation = computed(() => gambit.location as ShallowLocationDto);

const attendees = computed((): AttendeeDto[] => {
  if (!gambit || !Array.isArray(gambit.attendees)) return [];
  return gambit.attendees
    .filter((a) => a !== null)
    .filter((a) => a.isConfirmed)
    .map((a) => {
      const checkIn = gambit.activeCheckIns?.find((v) => v.userId === a.id);
      if (!checkIn) return { ...a, isCheckedIn: false };
      if (checkIn.end === null) return { ...a, isCheckedIn: true };
      return { ...a, isCheckedIn: isAfter(new Date(), new Date(checkIn.end)) ? false : true };
    })
    .sort((a, b) => attendeeRank(a) - attendeeRank(b));
});

/** You first, then whoever runs the gambit. */
const attendeeRank = (attendee: AttendeeDto): number => {
  if (user.value?.id && attendee.id === user.value.id) return 0;
  return attendee.id === gambit.ownerId || attendee.isOrganizer ? 1 : 2;
};

const invitees = computed(() => {
  if (!gambit) return [];
  if (!gambit.invitations?.length) return [];
  return gambit.invitations
    .filter((inv) => {
      if (!inv.user || !user.value) return false;
      return inv.user.id === user.value.id || inv.invitationOwnerId === user.value.id;
    })
    .filter((inv) => inv.accepted !== false)
    .map((inv) => inv.user)
    .filter((u) => u !== undefined);
});

const substantiveHandshakes = computed(() =>
  gambit.handshakes.filter(
    (h) => h.isConfirmed && (h.initiatorId || h.initiatorImageBlobHash || h.responderId || h.responderImageBlobHash),
  ),
);

const partiallyAnonimizedHandshakes = computed(() =>
  gambit.handshakes.filter((h) => h.isConfirmed && (h.initiator || h.responder)),
);

const gambitTags = computed(() =>
  GambitHelper.getGambitsTags(gambit as unknown as BaseGambitDto, {
    size: isMobile.value ? GamChipSize.X_SMALL : GamChipSize.SMALL,
    showChessSets: false,
    showRating: false,
    highlightAttendance: true,
  }),
);

const dateType = computed(() => GambitHelper.getGambitsDateType(gambit.start, false, gambit.end));

const formattedTime = computed(() => {
  const tz = gambit.timezone || undefined;
  if (dateType.value === GamDateType.NOW || dateType.value === GamDateType.SOON) {
    const timePart = GambitHelper.formatTime(gambit.start, { omitDay: true }, tz);
    const label = dateType.value === GamDateType.NOW ? 'Now' : 'Soon';
    return [label, ...timePart].join(' · ');
  }
  const parts = GambitHelper.formatTime(gambit.start, {}, tz);
  return parts.join(' · ');
});

const isTimeSoon = computed(() => {
  return (
    dateType.value === GamDateType.NOW ||
    dateType.value === GamDateType.SOON ||
    dateType.value === GamDateType.TODAY ||
    dateType.value === GamDateType.TOMORROW
  );
});

const hasCustomTitle = computed(() => {
  if (!gambit.title) return false;
  const locName = gambit.location?.name;
  if (!locName) return true;
  if (gambit.title === locName) return false;
  if (gambit.title.startsWith(`${locName} at `)) return false;
  return true;
});

const concludedAgo = computed(() =>
  formatDistanceToNow(new Date(gambit.end), { addSuffix: true }).replace(/about/g, ''),
);

const circleLocation = computed((): BaseLocationDto | null => {
  if (!gambit.location) return null;
  const loc = gambit.location;
  return {
    id: loc.id,
    coordinates: loc.coordinates,
    type: loc.type,
    chessFeatures: loc.chessFeatures,
    generalFeatures: loc.generalFeatures,
    private: loc.private,
    favorite: loc.favorite,
    isVetted: loc.isVetted,
    name: loc.name,
    address: loc.address,
    timezone: loc.timezone,
    coverBlobHash: loc.coverBlobHash,
    activeChessboardCount: 0,
    nextGambits: isConcluded.value ? [] : ([{ id: gambit.id, start: gambit.start }] as unknown as NextGambitDto[]),
  };
});

// --- Actions ---

const isJoining = ref(false);
const isLeaving = ref(false);
const isAccepting = ref(false);
const isDeleting = ref(false);
const isCheckingIn = ref(false);
const isCheckingOut = ref(false);

const isHappeningNow = computed(() => isInGambitCheckInWindow(gambit.start, gambit.end));

const isCheckedIn = computed(
  () => !!user.value?.id && !!gambit.activeCheckIns?.some((checkIn) => checkIn.userId === user.value?.id),
);

const showJoinButton = computed(
  () => !isConcluded.value && !gambit.isAttending && ((gambit.isSpark && gambit.isJoinable) || !gambit.isSpark),
);

const showCheckInButton = computed(() => gambit.isAttending && isHappeningNow.value && gambit.location);

const handleJoinGambit = async () => {
  if (!gambit || isJoining.value) return;
  isJoining.value = true;
  await gambitStore.joinGambit(gambit.id);
  await gambitStore.fetch(gambit.id);
  isJoining.value = false;
  await sparkStore.fetchIntents({ refreshExisting: true });
};

const { isLeaveConfirmOpen, leaveConfirmTitle, requestLeaveGambit, dismissLeaveConfirm, confirmLeaveGambit } =
  useLeaveGambitConfirm();

const handleLeaveGambit = async () => {
  if (!gambit || isLeaving.value) return;
  await requestLeaveGambit(gambit, performLeaveGambit);
};

const performLeaveGambit = async () => {
  if (!gambit || isLeaving.value) return;
  isLeaving.value = true;
  const { removed } = await gambitStore.leaveGambit(gambit.id);
  if (removed) {
    isLeaving.value = false;
    return;
  }
  await gambitStore.fetch(gambit.id);
  isLeaving.value = false;
  await sparkStore.fetchIntents({ refreshExisting: true });
};

const handleAccept = async () => {
  if (!gambit) return;
  isAccepting.value = true;
  await gambitStore.acceptChallenge(gambit.id);
  isAccepting.value = false;
};

const handleDelete = async () => {
  isDeleting.value = true;
  const toastMessage = isChallenger.value
    ? localize('gambit.challenge.actions.withdraw.success')
    : localize('gambit.challenge.actions.decline.success');
  const { removed } = await gambitStore.deleteGambit(gambit.id);
  if (!removed) {
    isDeleting.value = false;
    useToast().error(localize('gambit.actions.remove_error'));
    return;
  }
  useToast().info(toastMessage);
};

const handleChange = () => {
  const location = gambit.location;
  const coords = {
    lat: location?.coordinates?.lat || currentLocation.value.lat,
    lng: location?.coordinates?.lng || currentLocation.value.lng,
  };
  const zoom = location?.coordinates?.lat ? 14 : currentZoom.value;
  const challengedId = gambit.isChallenge
    ? isChallenger.value
      ? gambit.invitations[0]?.userId
      : gambit.ownerId
    : undefined;

  mapStore.enterProposalMode({
    challengeUserId: challengedId ?? undefined,
    editGambitId: gambit.id,
    editGambitIsChallenge: gambit.isChallenge,
    editGambitStart: gambit.start,
    editGambitEnd: gambit.end,
  });

  if (location?.id) {
    mapStore.setMapProposalLocation({
      mapLocationType: MapLocationType.LOCATION,
      id: location.id,
      name: location.name || '',
      coordinates: location.coordinates,
      address: location.address || '',
      type: location.type,
    });
  }

  router.push({ name: 'locations', query: { ...coords, zoom } });
};

const handleCheckIn = async () => {
  if (!gambit.location || isCheckingIn.value) return;
  isCheckingIn.value = true;
  const result = await gambits.locationService.checkIn(gambit.location.id, { gambitId: gambit.id });
  if (!result?.data) {
    isCheckingIn.value = false;
    return;
  }
  gambits.locationService.updateMarkerCheckInStatus(gambit.location.id, true);
  navigationStore.setActiveCheckIn({
    locationId: gambit.location.id,
    locationName: gambit.location.name ?? '',
    coordinates: gambit.location.coordinates,
    gambitId: gambit.id,
    checkedInAt: result.data.start,
  });
  await gambitStore.fetch(gambit.id);
  isCheckingIn.value = false;
};

const handleCheckOut = async () => {
  if (!gambit.location || isCheckingOut.value) return;
  isCheckingOut.value = true;
  await gambits.locationService.checkOut(gambit.location.id);
  gambits.locationService.updateMarkerCheckInStatus(gambit.location.id, false);
  navigationStore.clearActiveCheckIn();
  await gambitStore.fetch(gambit.id);
  isCheckingOut.value = false;
};

// --- Chat ---

watch(
  () => gambit,
  async (nextGambit) => {
    if (
      (chat.value && chat.value.id === nextGambit.chatId && areMessagesInitialized.value) ||
      !nextGambit.isAttending
    ) {
      return;
    }
    if (!nextGambit.chatId && isBefore(nowDate.value, new Date(nextGambit.end))) {
      const response = await gambits.chatService.createGambitChat(nextGambit.id);
      if (response.data) chatStore.fetch(response.data.id);
    } else if (typeof nextGambit.chatId === 'string') {
      chatStore.fetch(nextGambit.chatId);
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => chatStore.clear());

const hasUnreadChat = computed(() => gambit.hasUnreadChatMessages && !isChatOpen.value);
const unreadChatCountLabel = computed(() => {
  const count = gambit.unreadChatMessagesCount ?? 0;
  return count > 0 ? Math.min(count, 99) : '';
});

// Watching the route rather than the button also covers links that arrive with the chat open, like a push tap.
watch(
  () => [isChatOpen.value, gambit.chatId] as const,
  ([isOpen, chatId]) => {
    if (!isOpen || !chatId) return;
    gambits.chatService.markChatAsRead(chatId);
    sparkStore.gambitChanged(gambit.id, { kind: 'chatRead' });
  },
  { immediate: true },
);

const handleChatOpen = () => {
  router.replace({ ...route, query: { ...route.query, chat: 'open' } });
};

const handleChatClose = () => {
  router.replace({ ...route, query: { ...route.query, chat: undefined } });
};

// --- Chess Sets ---

const showChessSetPopover = ref(false);
const attendeeChessSetCount = ref(0);

const playerChessSets = computed(() => gambit.attendeeChessSetCount || 0);
const spotChessSets = computed(() => {
  if (!(gambit.location?.chessFeatures || []).includes(LocationChessFeature.CHESS_SET)) return 0;
  return gambit.location?.chessSetCount || 0;
});

const myChessSetCount = computed(() => {
  if (!gambit || !Array.isArray(gambit.attendees)) return 0;
  const me = gambit.attendees.find((a) => a?.id === user.value?.id);
  return me?.chessSetCount ?? 0;
});

watch(
  myChessSetCount,
  (val) => {
    attendeeChessSetCount.value = val;
  },
  { immediate: true },
);

/** Fewer boards than one per pair of attendees, counting the spot's own. */
const isShortOfChessSets = computed(
  () => playerChessSets.value + spotChessSets.value < Math.ceil(gambit.attendeeCount / 2),
);

const openChessSetPopover = () => {
  attendeeChessSetCount.value = myChessSetCount.value;
  showChessSetPopover.value = true;
};

const confirmChessSet = async (count: number) => {
  if (!gambit.id) return;
  await gambitStore.updateAttendance(gambit.id, count);
  showChessSetPopover.value = false;
};

// --- Share & More ---

const handleShareGambit = async () => {
  const shareUrl = `${window.location.origin}/share/gambit/${gambit.id}`;
  const locationName = gambit.location?.name || 'Chess';
  const title = `Chess at ${locationName} — ${formattedTime.value}`;
  const text = `${gambit.attendeeCount} going. Join now!`;

  if (navigator.share) {
    try {
      await navigator.share({ title, text, url: shareUrl });
      return;
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
    }
  }

  try {
    await navigator.clipboard.writeText(shareUrl);
    useToast().info('Copied.');
  } catch {
    /* clipboard write failed */
  }
};

const handleClose = () => {
  emit('close');
};

const handleOpenSpot = () => {
  if (!gambit.location?.id) return;
  router.push({
    name: 'locations',
    query: {
      lat: gambit.location.coordinates?.lat,
      lng: gambit.location.coordinates?.lng,
      zoom: '15',
    },
  });
};

function getOverlayInviteInitialPlayers(): ChosenPlayer[] {
  const currentUserId = userDetails.value?.id;
  const players: ChosenPlayer[] = [];
  const seenIds = new Set<string>();

  for (const invitation of gambit.invitations || []) {
    if (invitation.invitationOwnerId !== currentUserId) continue;
    if (seenIds.has(invitation.userId)) continue;
    seenIds.add(invitation.userId);

    const invUser = invitation.user;
    players.push({
      id: invitation.userId,
      username: invUser?.username || '',
      firstName: invUser?.firstName || '',
      lastName: invUser?.lastName || '',
      rating: invUser?.rating || 0,
      profileImageBlobHash: invUser?.profileImageBlobHash ?? null,
    });
  }

  return players;
}

const { open: openInviteModal, close: closeInviteModal } = useModal({
  component: InvitePlayers,
  attrs: {
    gambitId: gambit.id,
    participantIds: [
      ...gambit.invitations.map((i) => i.userId),
      ...gambit.attendees.filter((a) => a?.id).map((a) => a!.id),
    ],
    initialPlayers: getOverlayInviteInitialPlayers(),
    onClose: () => closeInviteModal(),
  },
});

const showReportDialog = ref(false);
const qrCodeDialog = ref<InstanceType<typeof GamDialog>>();

const getQrCodeValue = (id: string) => {
  return `${window.location.origin}/gambit/${id}`;
};

const getMoreItems = computed((): GamMoreType => {
  const items: GamMoreItem[] = [];
  const isAdmin = userDetails.value?.isAdmin;
  const isGambitOwner = isCreator.value || isOrganizer.value;
  const isStillEditable = isBefore(nowDate.value, new Date(new Date(gambit.end).getTime() + 24 * 60 * 60 * 1000));

  if (gambit.isAttending && gambit.location) {
    items.push({
      label: 'app.more.navigate',
      onClick: () => {
        const lat = gambit.location?.coordinates?.lat;
        const lng = gambit.location?.coordinates?.lng;
        if (lat && lng) {
          window.open(`https://www.google.com/maps/dir//${lat},${lng}`, '_blank');
        }
      },
    });
  }

  items.push({
    label: 'app.more.share.location',
    onClick: handleShareGambit,
  });

  if (!isPendingChallenge.value && !isConcluded.value) {
    items.push({
      label: 'app.more.qrcode',
      onClick: () => qrCodeDialog.value?.show(),
    });
  }

  if (((isGambitOwner && isStillEditable) || isAdmin) && !isChallenge.value) {
    items.push({
      label: 'app.more.edit',
      testId: 'app.more.edit',
      onClick: handleChange,
    });

    const destroy = isConcluded.value ? 'delete' : 'cancel';
    items.push({
      label: `app.more.${destroy}`,
      confirmation: localizeWithValues(`app.more.${destroy}_confirmation`, ['this IRL']),
      onClick: handleDelete,
    });
  }

  if (!isOrganizer.value || (isPendingChallenge.value && !isChallenger.value)) {
    items.push({
      label: 'app.more.report',
      onClick: () => (showReportDialog.value = true),
    });
  }

  return {
    items,
    size: GamButtonSize.SMALL,
    position: GamMorePosition.LEFT_BELOW,
    icon: GamIconName.DOTS_V,
    variant: GamButtonVariant.STEKLO,
  };
});
</script>

<style scoped lang="scss">
@use '@/ui/css/mixins' as *;
.GambitOverlay {
  position: fixed;
  inset: 0;
  z-index: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.gambit-card {
  position: relative;
  width: 380px;
  max-width: calc(100vw - 2rem);
  height: min(650px, calc(100vh - 4rem));
  border-radius: 1.5rem;
  border: 1px solid var(--color-white-10);
  background: var(--color-dark-700);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &.is-mobile {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    max-width: 100%;
    height: 85vh;
    border-radius: 1.5rem 1.5rem 0 0;
    will-change: transform;

    .gambit-scroll {
      padding-bottom: var(--safe-bottom-offset);
    }
  }
}

.gambit-scroll {
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: none;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  position: relative;
  z-index: 1;

  &::-webkit-scrollbar {
    display: none;
  }

  &.chat-open {
    overflow: hidden;
  }
}

.gambit-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.gambit-hero {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 50%;
  overflow: hidden;
  background: linear-gradient(var(--color-dark-500), var(--color-dark-700));
}

.gambit-hero-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  mask-image: linear-gradient(rgb(0, 0, 0) 50%, transparent);
}

.gambit-spot-info {
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
}

.gambit-spot-circle {
  --circle-size: 72px;
  width: auto;
  max-width: 100%;
  gap: 0.5rem;

  :deep(.circle-name) {
    font-size: 16px;
    -webkit-line-clamp: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 260px;
  }
}

.gambit-owner-actions {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.gambit-time-line {
  text-align: center;
  font-size: 17px;
  font-weight: 600;
  color: var(--color-white);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin-top: -0.25rem;

  &.is-soon {
    color: var(--color-pink);
  }
}

.gambit-actions-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 0.25rem 0;
  margin-bottom: 0.75rem;

  /* Decline, Change, Accept, and Withdraw match the Play disc. Width stays on the label. */
  :deep(.gam-button-container:not(.is-icon)) {
    height: 3.5rem;
    min-height: 3.5rem;
  }
}

/* Unstacked, the wrappers must not introduce a layout box so the buttons stay
   on the single centred row. */
.gambit-actions-primary,
.gambit-actions-secondary {
  display: contents;
}

.gambit-actions-row.is-stacked {
  flex-direction: column;
  gap: 0.75rem;

  .gambit-actions-primary,
  .gambit-actions-secondary {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
  }
}

.gambit-action-btn {
  width: 3rem;
  height: 3rem;
  @include toolbar-action;

  /* Gambit card: chess icon tops out at 1.3rem inside a 2.9rem circle. This
     circle is 3rem, so the board keeps that same share of the disc. */
  &.has-status :deep(.svg-container) {
    font-size: calc(3rem * 1.3 / 2.9) !important;
    width: calc(3rem * 1.3 / 2.9) !important;
    height: calc(3rem * 1.3 / 2.9) !important;
    min-width: calc(3rem * 1.3 / 2.9) !important;
  }

  .unread-indicator {
    position: absolute;
    top: -2px;
    right: -2px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 14px;
    height: 14px;
    padding: 0 3px;
    box-sizing: border-box;
    border-radius: 14px;
    background-color: var(--color-pink);
    color: var(--color-dark-700);
    font-size: 8px;
    font-weight: bold;
    pointer-events: none;
  }
}

// Sized to the spark cards' Play CTA, which tops out at 3.5rem. The secondary
// actions next to it keep their own 3rem circles.
.gambit-play-cta {
  // Both min dimensions are needed: GamButton's own icon rule is more specific
  // than this one, so min-width alone would stretch the circle into an ellipse.
  :deep(.gam-button-container.is-icon) {
    border-radius: 50% !important;
    width: 3.5rem !important;
    height: 3.5rem !important;
    min-width: 3.5rem !important;
    min-height: 3.5rem !important;
    padding: 0 !important;

    .icon {
      position: absolute !important;
      left: 50% !important;
      top: 50% !important;
      transform: translate(-50%, -50%) !important;
    }

    .svg-container {
      font-size: 24px !important;
      width: 24px !important;
      height: 24px !important;
    }
  }
}

@include play-cta;

.gambit-concluded-label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-pink);
  font-weight: 300;
  font-size: 13px;
  padding: 0.25rem 0;
}

.gambit-description {
  text-align: center;
}

.gambit-title {
  display: inline-flex;
  align-self: center;
  padding: 0.375rem 0.75rem;
  border-radius: 4px;
  background: var(--color-white-10);
  font-size: 16px;
  font-weight: 700;
  color: var(--color-white);
  text-align: center;
  line-height: 1.3;
}

.gambit-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--spacing-xs);
}

.gambit-section-header {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.25rem;
}

.gambit-section-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-white);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.gambit-section-line {
  border: none;
  border-top: 1px solid var(--color-white-10);
  margin: 0;
}

.gambit-attendees {
  margin-bottom: 0.5rem;
}

.gambit-challenge-attendees {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);

  > .is-first {
    order: -1;
  }
}

.gambit-handshake-board {
  margin-top: 4px;
}

// --- Chat Drawer ---

.gambit-chat-drawer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  top: 25%;
  z-index: 10;
  border-radius: 1rem 1rem 0 0;
  overflow: hidden;
  background: var(--color-dark-700);
  border-top: 1px solid var(--color-white-10);

  :deep(.chat) {
    height: 100%;

    .layout {
      height: 100%;
    }
  }
}

.chat-drawer-enter-active,
.chat-drawer-leave-active {
  transition: transform 250ms ease;
}

.chat-drawer-enter-from,
.chat-drawer-leave-to {
  transform: translateY(100%);
}

// --- Close ---

.gambit-close {
  position: absolute;
  bottom: var(--safe-bottom-offset);
  left: 50%;
  transform: translateX(-50%);
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 200ms linear;
  z-index: 1;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  @include press($transform: translateX(-50%));
}

// --- Overlay Transitions ---

.gambit-overlay-enter-active,
.gambit-overlay-leave-active {
  transition: opacity 200ms ease;

  .gambit-card {
    transition: transform 200ms ease;
  }
}

.gambit-overlay-enter-from,
.gambit-overlay-leave-to {
  opacity: 0;

  .gambit-card {
    transform: translateY(20px);
  }
}

.gambit-overlay-enter-from .is-mobile,
.gambit-overlay-leave-to .is-mobile {
  transform: translateY(100%);
}
</style>
